import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, ChevronLeft, KeyRound, Trash2, Copy } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { API, ax, errorText, MARKETPLACES } from "./common";
import RouteProfilesPanel from "./RouteProfilesPanel";
import ResetPasswordDialog from "./ResetPasswordDialog";

const EMPTY_TENANT = { code: "", name: "", multi_profile_enabled: false };
const EMPTY_ADMIN = { name: "", email: "", password: "" };

function TenantDetail({ tenant, onBack, onChanged, showMsg, refreshTick }) {
  const { t } = useLang();
  const [adminForm, setAdminForm] = useState(null);

  const [admins, setAdmins] = useState([]);
  const [resetFor, setResetFor] = useState(null);
  const [editAdmin, setEditAdmin] = useState(null);
  const [deleteAdmin, setDeleteAdmin] = useState(null);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(tenant.id);
      showMsg("success", t("adm.tn.id_copied"));
    } catch { showMsg("error", t("adm.error")); }
  };

  const loadAdmins = useCallback(async (silent = false) => {
    try {
      const { data } = await axios.get(`${API}/tenants/${tenant.id}/admins`, ax);
      setAdmins(data);
    } catch (e) { if (!silent) showMsg("error", errorText(e, t("adm.error"))); }
  }, [tenant.id, showMsg, t]);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);
  useEffect(() => { if (refreshTick) loadAdmins(true); }, [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const createAdmin = async () => {
    try {
      await axios.post(`${API}/tenants/${tenant.id}/admins`, adminForm, ax);
      showMsg("success", t("adm.tn.admin_created"));
      setAdminForm(null);
      loadAdmins();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const saveAdmin = async () => {
    try {
      await axios.put(`${API}/tenants/${tenant.id}/admins/${editAdmin.id}`, { name: editAdmin.name, email: editAdmin.email }, ax);
      showMsg("success", t("adm.us.updated"));
      setEditAdmin(null);
      loadAdmins();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const removeAdmin = async () => {
    const target = deleteAdmin;
    setDeleteAdmin(null);
    try {
      await axios.delete(`${API}/tenants/${tenant.id}/admins/${target.id}`, ax);
      showMsg("success", t("adm.us.deleted"));
      loadAdmins();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const setStatus = async (suspend) => {
    try {
      await axios.post(`${API}/tenants/${tenant.id}/${suspend ? "suspend" : "activate"}`, {}, ax);
      showMsg("success", t("adm.tn.status_changed"));
      onChanged();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const setFlag = async (field, value) => {
    try {
      await axios.put(`${API}/tenants/${tenant.id}`, { [field]: value }, ax);
      showMsg("success", t("adm.tn.updated"));
      onChanged();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const active = tenant.status === "ACTIVE";

  const toggleMarketplace = async (key, enabled) => {
    const next = enabled ? [...tenant.marketplaces, key] : tenant.marketplaces.filter(k => k !== key);
    try {
      await axios.put(`${API}/tenants/${tenant.id}`, { marketplaces: next }, ax);
      showMsg("success", t("adm.tn.updated"));
      onChanged();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ChevronLeft className="w-4 h-4" /> {t("common.back")}
      </button>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-bold text-slate-900">{tenant.name}</p>
            <p className="text-xs font-mono text-slate-400">{tenant.code}</p>
            <p className="text-xs font-mono text-slate-400 flex items-center gap-1" data-testid="tenant-id">
              {t("adm.tn.id")}: <span className="select-all">{tenant.id}</span>
              <button type="button" onClick={copyId} title={t("adm.tn.copy_id")} className="text-slate-400 hover:text-slate-700">
                <Copy className="w-3 h-3" />
              </button>
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setStatus(active)}>
            {t(active ? "adm.suspend" : "adm.activate")}
          </Button>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Switch checked={tenant.multi_profile_enabled} onCheckedChange={v => setFlag("multi_profile_enabled", v)} />
          {t("adm.tn.multi_profile")}
        </label>
        <div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Switch checked={tenant.inbound_enabled} onCheckedChange={v => setFlag("inbound_enabled", v)} data-testid="tenant-inbound-switch" />
            {t("adm.tn.inbound")}
          </label>
          <p className="text-xs text-slate-500 mt-1">{t("adm.tn.inbound_hint")}</p>
        </div>
        <div className="space-y-1.5">
          <p className="text-sm font-semibold text-slate-800">{t("adm.tn.marketplaces")}</p>
          {MARKETPLACES.map(key => (
            <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={tenant.marketplaces.includes(key)} onCheckedChange={checked => toggleMarketplace(key, checked === true)} />
              {t(`adm.marketplace.${key}`)}
            </label>
          ))}
          <p className="text-xs text-slate-500">{t("adm.tn.marketplaces_hint")}</p>
        </div>
        <div className="space-y-1.5">
          <p className="text-sm font-semibold text-slate-800">{t("adm.tn.admins")}</p>
          {admins.length === 0 && <p className="text-xs text-slate-500">{t("adm.tn.no_admins")}</p>}
          {admins.map(a => (
            <div key={a.id} className="flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{a.name}</p>
                <p className="text-xs text-slate-500 truncate">{a.email}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setResetFor(a)} className="gap-1.5">
                <KeyRound className="w-3.5 h-3.5" /> {t("adm.us.reset_password")}
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                onClick={() => setEditAdmin({ id: a.id, name: a.name || "", email: a.email || "" })} aria-label={t("common.edit")}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                onClick={() => setDeleteAdmin(a)} aria-label={t("common.delete")}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
        <Button size="sm" variant="outline" disabled={!active} onClick={() => setAdminForm({ ...EMPTY_ADMIN })} className="gap-1.5">
          <Plus className="w-4 h-4" /> {t("adm.tn.add_admin")}
        </Button>
      </div>

      <RouteProfilesPanel tenant={tenant} showMsg={showMsg} refreshTick={refreshTick} />
      <ResetPasswordDialog admin={resetFor} tenantId={tenant.id} onClose={() => setResetFor(null)} showMsg={showMsg} />

      <Dialog open={!!editAdmin} onOpenChange={(open) => !open && setEditAdmin(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("adm.us.edit")}</DialogTitle></DialogHeader>
          {editAdmin && (
            <div className="space-y-3">
              {[["name", "common.name", "text"], ["email", "admin.email", "email"]].map(([field, key, type]) => (
                <div key={field} className="space-y-1.5">
                  <Label>{t(key)} *</Label>
                  <Input type={type} autoComplete="off" value={editAdmin[field]}
                    onChange={e => setEditAdmin(f => ({ ...f, [field]: e.target.value }))} className="h-10" />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAdmin(null)}>{t("common.cancel")}</Button>
            <Button onClick={saveAdmin} disabled={!editAdmin?.name.trim() || !editAdmin?.email.trim()}
              className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteAdmin} onOpenChange={() => setDeleteAdmin(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("adm.delete_title", { name: deleteAdmin?.name ?? "" })}</AlertDialogTitle>
            <p className="text-sm text-muted-foreground">{t("adm.delete_warning")}</p>
            {admins.length === 1 && (
              <p className="text-sm font-medium text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{t("adm.tn.last_admin_warning")}</p>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={removeAdmin}>{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!adminForm} onOpenChange={(open) => !open && setAdminForm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("adm.tn.add_admin")}</DialogTitle></DialogHeader>
          {adminForm && (
            <div className="space-y-3">
              {[["name", "common.name", "text"], ["email", "admin.email", "email"], ["password", "admin.password", "password"]].map(([field, key, type]) => (
                <div key={field} className="space-y-1.5">
                  <Label>{t(key)} *</Label>
                  <Input type={type} autoComplete="off" value={adminForm[field]}
                    onChange={e => setAdminForm(f => ({ ...f, [field]: e.target.value }))} className="h-10" />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={createAdmin} disabled={!adminForm?.name || !adminForm?.email || !adminForm?.password}
              className="bg-blue-600 hover:bg-blue-700">{t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function TenantsTab({ showMsg, refreshTick = 0 }) {
  const { t } = useLang();
  const [tenants, setTenants] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async (silent = false) => {
    try {
      const { data } = await axios.get(`${API}/tenants`, ax);
      setTenants(data);
    } catch (e) { if (!silent) showMsg("error", errorText(e, t("adm.error"))); }
  }, [showMsg, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshTick) load(true); }, [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/tenants/${editingId}`, { name: form.name }, ax);
        showMsg("success", t("adm.tn.updated"));
      } else {
        await axios.post(`${API}/tenants`, form, ax);
        showMsg("success", t("adm.tn.created"));
      }
      setForm(null);
      load();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const selected = tenants.find(x => x.id === selectedId);
  if (selected) {
    return <TenantDetail tenant={selected} onBack={() => setSelectedId(null)} onChanged={load} showMsg={showMsg} refreshTick={refreshTick} />;
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => { setEditingId(null); setForm({ ...EMPTY_TENANT }); }} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4" /> {t("adm.tn.new")}
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-50">
        {tenants.length === 0 && <p className="p-8 text-center text-slate-400 text-sm">{t("adm.tn.empty")}</p>}
        {tenants.map(x => (
          <div key={x.id} className="flex items-center gap-3 px-4 py-3">
            <button onClick={() => setSelectedId(x.id)} className="flex-1 min-w-0 text-left">
              <p className="font-semibold text-slate-900 text-sm truncate">{x.name} <span className="font-mono text-xs text-slate-400">{x.code}</span></p>
              <p className="font-mono text-[11px] text-slate-400 truncate">{x.id}</p>
              <p className="text-xs text-slate-500">{t("adm.tn.open")}</p>
            </button>
            <span className={`text-xs font-medium px-2 py-1 rounded-lg ${x.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {t(x.status === "ACTIVE" ? "adm.active" : "adm.suspended")}
            </span>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
              onClick={() => { setEditingId(x.id); setForm({ code: x.code, name: x.name, multi_profile_enabled: x.multi_profile_enabled }); }}
              aria-label={t("common.edit")}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!form} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t(editingId ? "adm.tn.edit" : "adm.tn.new")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>{t("adm.code")} *</Label>
                <Input value={form.code} disabled={!!editingId} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} className="h-10 font-mono lowercase" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("common.name")} *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-10" />
              </div>
              {!editingId && (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Switch checked={form.multi_profile_enabled} onCheckedChange={v => setForm(f => ({ ...f, multi_profile_enabled: v }))} />
                  {t("adm.tn.multi_profile")}
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={save} disabled={!form?.name.trim() || (!editingId && !form?.code.trim())} className="bg-blue-600 hover:bg-blue-700">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
